import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { CodeSentResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { useRequestCode } from "@/shared/api/generated/auth-controller/auth-controller";
import { formatLocal, isCompletePhone, toE164 } from "@/shared/lib/phone";
import { Button, FormField, Input } from "@/shared/ui";
import { authErrorMessage } from "../model/errorMessage";

const schema = z.object({
  phone: z.string().refine(isCompletePhone, { message: "phoneIncomplete" }),
});
type PhoneForm = z.infer<typeof schema>;

interface PhoneStepProps {
  defaultPhone: string;
  onCodeSent: (phone: string, sent: CodeSentResponse) => void;
}

/** Step 1 of login: the customer enters a phone number and receives an SMS code. */
export function PhoneStep({ defaultPhone, onCodeSent }: PhoneStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const requestCode = useRequestCode();
  const form = useForm<PhoneForm>({
    resolver: zodResolver(schema),
    defaultValues: { phone: defaultPhone },
  });
  const fieldError = form.formState.errors.phone;

  const submit = form.handleSubmit((values) => {
    const phone = toE164(values.phone);
    requestCode.mutate(
      { data: { lc_phone: phone } },
      {
        onSuccess: (sent) => {
          onCodeSent(phone, sent);
        },
      },
    );
  });

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <FormField
        id="phone"
        label={t("auth:phoneLabel")}
        hint={t("auth:phoneHint")}
        error={fieldError && t("auth:errors.phoneIncomplete")}
      >
        {(field) => (
          <Input
            {...field}
            start="+998"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="90 123-45-67"
            {...form.register("phone", {
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                form.setValue("phone", formatLocal(event.target.value));
              },
            })}
          />
        )}
      </FormField>

      {requestCode.isError && (
        <p role="alert" className="text-sm text-danger">
          {authErrorMessage(requestCode.error, t)}
        </p>
      )}

      <Button type="submit" size="lg" loading={requestCode.isPending}>
        {t("auth:getCode")}
      </Button>
    </form>
  );
}
